// src/services/statsApi.js
//
// Admin istatistik uçlarının (/v2/admin/stats/...) tek toplandığı yer.
// Dördü de SALT OKUNUR; panel istediği sıklıkta çağırabilir. Tek istisna
// catalog-health: Google Play kataloğunu listelediği için 5-15 saniye sürer,
// bu yüzden ayrı ve yalnızca kullanıcı isteyince çağrılır.
//
// Verinin karakteri tasarımı belirliyor (kılavuz §1): canlıda 14 aya yayılmış
// ~192 sipariş var. Günlük grafik boş görünür, "bugün" kartları anlamsızdır,
// yüzde rozetleri bu hacimde gürültüdür. Varsayılan aralık son 90 gün.

import apiClient from './ApiClient';

// Metinler değişebilir, kodlar sözleşmedir.
const ERROR_MESSAGES = {
    INVALID_DATE: 'Tarih biçimi geçersiz (YYYY-AA-GG olmalı).',
    INVALID_BUCKET: 'Geçersiz zaman aralığı; gün, hafta veya ay seçin.',
    STATS_FAILED: 'İstatistikler hesaplanamadı (sunucu hatası).',
};

export const readStatsError = (err, fallback = 'Beklenmeyen bir hata oluştu.') => {
    const data = err?.response?.data;
    const code = data?.code || null;

    if (code && ERROR_MESSAGES[code]) return { code, message: ERROR_MESSAGES[code] };

    const text = data?.error || data?.message;
    if (text) return { code, message: text };

    const status = err?.response?.status;
    if (status === 401) return { code, message: 'Oturum geçersiz, tekrar giriş yapın.' };
    if (status === 403) return { code, message: 'Bu ekranı görmek için admin yetkisi gerekiyor.' };
    if (status === 404) return { code, message: 'İstatistik uçları bu sunucuda henüz yayında değil (404).' };

    return { code, message: fallback };
};

// BaseResponse zarfı: { status, message, data, error, code }
const unwrap = (responseData) => {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        return responseData.data;
    }
    return responseData;
};

// --- Tarih yardımcıları ---------------------------------------------------

export const toApiDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
};

/**
 * Hazır aralıklar. "Bugün/dün" bilerek yok: bu hacimde çoğu gün sıfır gösterir
 * ve panel boş görünür (kılavuz §1).
 */
export const RANGE_PRESETS = [
    { value: '30', label: 'Son 30 gün', build: () => ({ from: toApiDate(daysAgo(30)), to: toApiDate(new Date()) }) },
    { value: '90', label: 'Son 90 gün', build: () => ({ from: toApiDate(daysAgo(90)), to: toApiDate(new Date()) }) },
    { value: '365', label: 'Son 12 ay', build: () => ({ from: toApiDate(daysAgo(365)), to: toApiDate(new Date()) }) },
    {
        value: 'ytd',
        label: 'Bu yıl',
        build: () => ({ from: toApiDate(new Date(new Date().getFullYear(), 0, 1)), to: toApiDate(new Date()) }),
    },
];

export const DEFAULT_RANGE_PRESET = '90';

export const BUCKETS = [
    { value: 'day', label: 'Gün' },
    { value: 'week', label: 'Hafta' },
    { value: 'month', label: 'Ay' },
];

/** Zaman serisi ekseni etiketi. Kova tipine göre biçim değişir. */
export const bucketLabel = (isoDate, bucket) => {
    if (!isoDate) return '';
    const date = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) return isoDate;
    if (bucket === 'month') return date.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
};

// --- Biçimlendirme --------------------------------------------------------

const TRY_FORMAT = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INT_FORMAT = new Intl.NumberFormat('tr-TR');

export const formatCount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? INT_FORMAT.format(n) : '—';
};

export const formatTry = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? `₺${TRY_FORMAT.format(n)}` : '—';
};

export const formatEur = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? `€${TRY_FORMAT.format(n)}` : '—';
};

export const formatPercent = (value, digits = 1) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? `%${n.toFixed(digits).replace('.', ',')}` : '—';
};

export const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Bir tarihin üstünden kaç gün geçti (köprü ürünlerinde "bekle" kararı için). */
export const daysSince = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

// --- Uçlar ----------------------------------------------------------------

/** GET /v2/admin/stats/overview — KPI, huni, platform dağılımı, en çok satanlar. */
export const fetchOverview = async ({ from, to } = {}) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.get('/v2/admin/stats/overview', { params });
    return unwrap(response.data);
};

/** GET /v2/admin/stats/timeseries — boş kovalar sıfırla dolu gelir, ara doldurma yapma. */
export const fetchTimeseries = async ({ from, to, bucket = 'month' } = {}) => {
    const params = { bucket };
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.get('/v2/admin/stats/timeseries', { params });
    return unwrap(response.data);
};

/** GET /v2/admin/stats/operations — "şu an ne bozuk". Tarih parametresi almaz. */
export const fetchOperations = async () => {
    const response = await apiClient.get('/v2/admin/stats/operations');
    return unwrap(response.data);
};

/**
 * GET /v2/admin/stats/catalog-health
 * Google Play kataloğunu listeler: 5-15 saniye. Genel 20 sn'lik axios zaman aşımı
 * sınırda kalıyor, bu uca kendi payını veriyoruz.
 */
export const fetchCatalogHealth = async () => {
    const response = await apiClient.get('/v2/admin/stats/catalog-health', { timeout: 45000 });
    return unwrap(response.data);
};
